// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Organization } from '@/account/api/types';
import {
  CheckCircle,
  ErrorOutlineOutlined,
  HighlightOff,
  Settings,
  SyncOutlined,
} from '@/constants/hardfin-standard-icons';
import { QuickBooksOnlineLogo } from '@/icons/quickbooks-online-logo';
import { findIntegrationConfig } from '@/utilities/integration';
import {
  Box,
  Button,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

interface QuickBooksCRMRowProps {
  organization: Organization;
  isNetSuiteConnected: boolean;
  isEnabledForOrg: boolean;
  onOpenConnectionDialog: () => void;
}

interface SettingsButtonProps {
  onOpen: () => void;
  buttonLabel: string;
  text: string;
}

function SettingsButton(props: SettingsButtonProps) {
  return (
    <Typography sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        startIcon={<Settings color={'action'} fontSize={'small'} />}
        onClick={props.onOpen}
        variant={'outlined'}
      >
        {props.buttonLabel}
      </Button>
      <Typography sx={{ ml: 1 }}>{props.text}</Typography>
    </Typography>
  );
}

export function QuickBooksCRMRow(props: QuickBooksCRMRowProps) {
  const accountingConfig = findIntegrationConfig(
    props.organization.integrationConfigurations,
    'QBO'
  );
  const isQuickBooksConfiguration = accountingConfig !== null;

  // Type guard to properly discriminate the QuickBooks configuration
  const quickbooksConfig = isQuickBooksConfiguration
    ? accountingConfig.configurationState
    : null;

  const isConnected = quickbooksConfig?.connected ?? false;
  const companyName = quickbooksConfig?.companyName ?? null;
  const allScopesPresent = quickbooksConfig?.allScopesPresent ?? false;
  const lastSyncedAt = quickbooksConfig?.lastSyncedAt ?? null;

  const isDisabledByOtherERP =
    props.isNetSuiteConnected && !isQuickBooksConfiguration;
  const isUnavailable = !props.isEnabledForOrg;

  const DisabledCell = (cellProps: { text: string }) => (
    <TableCell>
      <Typography
        sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled' }}
      >
        <HighlightOff color={'disabled'} fontSize={'small'} sx={{ mr: 1 }} />
        {cellProps.text}
      </Typography>
    </TableCell>
  );

  if (isUnavailable) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Contact support to enable this integration" arrow>
              <QuickBooksOnlineLogo
                sx={{ width: 100, height: 24, opacity: 0.5 }}
              />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="QuickBooks Online integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="QuickBooks Online integration is not enabled for your organization, contact Hardfin support to activate" />
        <DisabledCell text="QuickBooks Online integration is not enabled for your organization, contact Hardfin support to activate" />
        <TableCell align={'right'}>{/* Unavailable - no settings */}</TableCell>
      </TableRow>
    );
  }

  if (isDisabledByOtherERP) {
    return (
      <TableRow sx={{ opacity: 0.8 }}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
              title="Only one accounting system can be connected at a time"
              arrow
            >
              <QuickBooksOnlineLogo
                sx={{ width: 100, height: 24, opacity: 0.5 }}
              />
            </Tooltip>
          </Box>
        </TableCell>
        <DisabledCell text="Not available while NetSuite is already connected" />
        <DisabledCell text="Not available while NetSuite is already connected" />
        <DisabledCell text="Not available while NetSuite is already connected" />
        <TableCell align={'right'}>{/* Disabled - no settings */}</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QuickBooksOnlineLogo sx={{ width: 100, height: 24 }} />
        </Box>
      </TableCell>
      <TableCell>
        {!isConnected ? (
          <SettingsButton
            onOpen={props.onOpenConnectionDialog}
            buttonLabel="Connect"
            text={'Not connected'}
          />
        ) : allScopesPresent ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle color={'success'} fontSize={'small'} sx={{ mr: 1 }} />{' '}
            Connected{companyName ? ` to ${companyName}` : ''}
          </Typography>
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              startIcon={
                <ErrorOutlineOutlined
                  color={'warning'}
                  fontSize={'small'}
                  sx={{ mr: 1 }}
                />
              }
              onClick={props.onOpenConnectionDialog}
              variant={'outlined'}
            >
              Partially connected
            </Button>
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {/* No configuration available yet */}
        {isConnected ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            Managed by Hardfin
          </Typography>
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <HighlightOff color={'action'} fontSize={'small'} sx={{ mr: 1 }} />
            Not configured
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {isConnected && lastSyncedAt !== null ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <SyncOutlined color={'success'} fontSize={'small'} sx={{ mr: 1 }} />
            Last synced at{' '}
            {lastSyncedAt.setZone(props.organization.timezone).toLocaleString({
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </Typography>
        ) : isConnected ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <HighlightOff color={'action'} fontSize={'small'} sx={{ mr: 1 }} />
            Not synced yet
          </Typography>
        ) : (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            <HighlightOff color={'action'} fontSize={'small'} sx={{ mr: 1 }} />
            Not syncing
          </Typography>
        )}
      </TableCell>
      <TableCell align={'right'}>
        {/* No settings available - configuration managed by Hardfin */}
      </TableCell>
    </TableRow>
  );
}
